import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getQuote, quoteTotal } from '../services/quotesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQuote(await getQuote(id));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando cotización…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!quote) return <EmptyState title="Cotización no encontrada" />;

  const items = Array.isArray(quote.items) ? quote.items : [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/cotizaciones">Cotizaciones</Link> / N° {quote.cotizacion}
          </p>
          <h1>Cotización N° {quote.cotizacion}</h1>
          <p className="muted">
            {formatDate(quote.fecha)} · Cliente {quote.cliente || '—'}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Total</p>
          <p className="stat-value">{formatMoney(quoteTotal(quote))}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Ítems</p>
          <p className="stat-value">{items.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {quote.cancelado ? <Badge tone="muted">Cancelada</Badge> : <Badge tone="ok">Vigente</Badge>}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Vendedor</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {quote.vendedor || '—'}
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Productos</h2>
        </header>
        {items.length === 0 ? (
          <EmptyState title="Sin ítems" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cant.</th>
                  <th>Precio</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={`${it.uid || it.id || 'item'}-${idx}`}>
                    <td>{it.name || '—'}</td>
                    <td>{it.quantity ?? 0}</td>
                    <td>{formatMoney(it.price, it.currency)}</td>
                    <td>
                      {formatMoney(
                        it.subtotalAfterDiscounts ?? Number(it.price || 0) * Number(it.quantity || 0),
                        it.currency
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="muted">
        Para convertir a pedido (con descuento de stock FEFO) usa la app móvil:
        el módulo <code>convertirCotizacionEnPedido</code> del core no se reimplementa aquí.
      </p>
    </div>
  );
}
